import {
  BuildDetailsDto,
  LicenseStatus,
  MachineDto,
  OaiboxStatus,
  Tenant,
} from "@allbesmart/web-app-common";
import { UserRole } from "@allbesmart/web-app-keycloak-auth";
import { Component, NgZone, OnDestroy } from "@angular/core";
import Keycloak from "keycloak-js";
import { merge, Observable, Subscription, tap } from "rxjs";
import { LICENSE_STATUS_CONFIG } from "../../configs/license-status-config";
import { isAiRanType } from "../../helpers/oaibox-type";
import { LicenseStat } from "../../models/license/license-stat";
import { AiRanTelemetryService } from "../../services/ai-ran-telemetry.service";
import { BuildService } from "../../services/build.service";
import { OaiboxTelemetryService } from "../../services/oaibox-telemetry.service";
import { TenantManagementService } from "../../services/tenant-management.service";

@Component({
  selector: "oaibox-deployments-kpis",
  templateUrl: "./deployments-kpis.component.html",
  styleUrls: ["./deployments-kpis.component.css"],
})
export class DeploymentsKpisComponent implements OnDestroy {
  private readonly LICENSE_EXPIRY_WINDOW_MS = 30 * 24 * 60 * 60 * 1000;
  private readonly LICENSE_STATUS_ORDER = [
    LicenseStatus.ACTIVE,
    LicenseStatus.PERPETUAL,
    LicenseStatus.PAUSED,
    LicenseStatus.INACTIVE,
  ];

  private readonly isRoot: boolean;

  private buildDetails: BuildDetailsDto[] = [];
  private availableCommitId?: string;

  protected totalDeployments: number = 0;
  protected onlineDeployments: number = 0;
  protected offlineDeployments: number = 0;
  protected licenseStats: LicenseStat[] = [];

  protected totalMachines: number = 0;
  protected onlineMachines: number = 0;
  protected offlineMachines: number = 0;
  protected upToDateMachines: number = 0;
  protected versionedMachines: number = 0;
  protected upToDatePercentage?: number;

  private readonly changesSubscription: Subscription;

  constructor(
    keycloak: Keycloak,
    buildService: BuildService,
    ngZone: NgZone,
    private readonly tenantManagementService: TenantManagementService,
    private readonly oaiboxTelemetryService: OaiboxTelemetryService,
    private readonly aiRanTelemetryService: AiRanTelemetryService
  ) {
    this.isRoot = keycloak.hasRealmRole(UserRole.ROOT);

    // Same reference commit the machine rows compare against: root users get the build assigned to each machine,
    // everyone else the latest available build
    const buildChanges$: Observable<unknown> = this.isRoot
      ? buildService
          .getBuildDetails$()
          .pipe(tap((buildDetails) => (this.buildDetails = buildDetails)))
      : buildService
          .getAvailableBuildCommit$()
          .pipe(
            tap(
              (availableCommit) =>
                (this.availableCommitId = availableCommit?.commitId)
            )
          );

    // Telemetry changes are emitted outside the Angular zone
    this.changesSubscription = merge(
      this.oaiboxTelemetryService.getOaiboxStatusChanges$(),
      this.aiRanTelemetryService.getOaiboxStatusChanges$(),
      this.tenantManagementService.getTenants$(),
      buildChanges$
    ).subscribe(() =>
      ngZone.run(() => {
        const tenants = this.tenantManagementService.getTenants();
        this.computeDeploymentKpis(tenants);
        this.computeMachineKpis(tenants);
      })
    );
  }

  private computeDeploymentKpis(tenants: Tenant[]): void {
    this.totalDeployments = tenants.length;
    this.onlineDeployments = tenants.filter((tenant) =>
      tenant.registeredMachines.some((machine) => this.isMachineOnline(machine))
    ).length;
    this.offlineDeployments = this.totalDeployments - this.onlineDeployments;

    const now = Date.now();
    this.licenseStats = this.LICENSE_STATUS_ORDER.map((status) => {
      const licensedTenants = tenants.filter(
        (tenant) => tenant.license?.status === status
      );
      return {
        ...LICENSE_STATUS_CONFIG[status],
        count: licensedTenants.length,
        expiring:
          status === LicenseStatus.ACTIVE
            ? licensedTenants.filter((tenant) =>
                this.isExpiringSoon(tenant.license?.endTimestamp, now)
              ).length
            : undefined,
      };
    });
    this.licenseStats.push({
      label: "No License",
      color: "secondary",
      count: tenants.filter((tenant) => !tenant.license?.status).length,
    });
  }

  private computeMachineKpis(tenants: Tenant[]): void {
    const machines = tenants.flatMap((tenant) =>
      tenant.registeredMachines.map((machine) => ({ tenant, machine }))
    );

    this.totalMachines = machines.length;
    this.onlineMachines = machines.filter(({ machine }) =>
      this.isMachineOnline(machine)
    ).length;
    this.offlineMachines = this.totalMachines - this.onlineMachines;

    this.upToDateMachines = 0;
    this.versionedMachines = 0;
    machines.forEach(({ tenant, machine }) => {
      if (isAiRanType(machine.productDetails.oaiboxType)) return;

      const referenceCommitId = this.isRoot
        ? this.buildDetails.find(
            (build) =>
              build.tenantId === tenant.id && build.machineId === machine.id
          )?.commitId
        : this.availableCommitId;
      if (!referenceCommitId) return;

      this.versionedMachines++;
      if (
        this.oaiboxTelemetryService.getOaiboxTelemetry(machine.id)?.commitId ===
        referenceCommitId
      ) {
        this.upToDateMachines++;
      }
    });

    this.upToDatePercentage =
      this.versionedMachines > 0
        ? Math.round((this.upToDateMachines / this.versionedMachines) * 100)
        : undefined;
  }

  private isMachineOnline(machine: MachineDto): boolean {
    if (isAiRanType(machine.productDetails.oaiboxType)) {
      return (
        this.aiRanTelemetryService.getAiRanTelemetry(machine.id)?.status ===
        OaiboxStatus.ONLINE
      );
    }
    return (
      this.oaiboxTelemetryService.getOaiboxTelemetry(machine.id)?.status ===
      OaiboxStatus.ONLINE
    );
  }

  private isExpiringSoon(
    endTimestamp: number | undefined,
    now: number
  ): boolean {
    return (
      endTimestamp !== undefined &&
      endTimestamp >= now &&
      endTimestamp - now <= this.LICENSE_EXPIRY_WINDOW_MS
    );
  }

  ngOnDestroy(): void {
    this.changesSubscription.unsubscribe();
  }
}
